import { useRef, useState, useEffect } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import {
  Button,
  Box,
  Divider,
  MenuItem,
  Typography,
  Avatar,
  IconButton,
} from '@mui/material';
import Iconify from '../../components/Iconify';
import MenuPopover from '../../components/MenuPopover';
import { logoutUser } from '../../store/auth/api';
import { useDispatch } from 'react-redux';
import { MENU_OPTIONS } from '../../constants';
import { useAppSelector } from '../../store';
import { getAssetURl } from '../../utils/url';
// import { deleteToken, getMessaging } from 'firebase/messaging';

const AccountPopover = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [menuOptions, setMenuOptions] = useState([...MENU_OPTIONS]);
  const [imageError, setImageError] = useState(false);
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const loggedInUserInfo = useAppSelector((state) => state.auth.user);
  
  // Reset image error when user profile image changes
  useEffect(() => {
    setImageError(false);
  }, [loggedInUserInfo?.userProfileImage?.key]);
  const loggedInUserRole = loggedInUserInfo?.userRole;

  useEffect(() => {
    if (location?.pathname === '/organizations') {
      const arrayIndex = menuOptions.findIndex(
        (x: { label: string }) => x.label === 'label.switch_organization'
      );
      if (arrayIndex >= 0) {
        menuOptions.splice(arrayIndex, 1);
        setMenuOptions(menuOptions);
      }
    }
  }, [location]);

  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const handleLogout = async () => {
    // if (typeof window?.navigator?.serviceWorker !== 'undefined') {
    //   await deleteToken(getMessaging())
    //     .then(() => {
    //       console.info('Token deleted successfully');
    //     })
    //     .catch((error: any) => {
    //       console.error('Error deleting token:', error);
    //     });
    // }
    
    // Get the current user role before clearing localStorage
    const currentUserRole = loggedInUserRole;
    
    // Store/update the role in localStorage (preserve it, never erase)
    if (currentUserRole) {
      localStorage.setItem('lastUserRole', currentUserRole);
    }
    
    // Get the role to use for redirect (use current or fallback to stored)
    const lastUserRole = currentUserRole || localStorage.getItem('lastUserRole');
    
    // Clear all items except lastUserRole (preserve it)
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key !== 'lastUserRole') {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    // Ensure lastUserRole is still set after clearing
    if (lastUserRole) {
      localStorage.setItem('lastUserRole', lastUserRole);
    }
    
    await dispatch(logoutUser());
    
    // Redirect based on stored role
    if (lastUserRole === 'company') {
      navigate('/company-login');
    } else {
      navigate('/login');
    }
  };

  return (
    <>
      <IconButton
        ref={anchorRef}
        onClick={handleOpen}
        sx={{
          padding: 0,
          width: 44,
          height: 44,
          ...(open && {
            '&:before': {
              zIndex: 1,
              content: "''",
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              position: 'absolute',
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72),
            },
          }),
        }}
      >
          <Avatar
          alt={loggedInUserInfo?.name || 'User'}
          src={loggedInUserInfo?.userProfileImage?.key && !imageError ? getAssetURl(
              loggedInUserInfo?.userProfileImage?.key
          ) : undefined}
            imgProps={{ crossOrigin: 'anonymous' }}
          onError={() => {
            // Set error state when image fails to load
            setImageError(true);
          }}
            sx={{
              objectFit: 'contain',
              '& img': { objectFit: 'contain' },
            }}
        >
          {(!loggedInUserInfo?.userProfileImage?.key || imageError) 
            ? loggedInUserInfo?.name?.charAt(0)?.toUpperCase() || 'U'
            : null
          }
        </Avatar>
      </IconButton>

      <MenuPopover
        open={open}
        onClose={handleClose}
        anchorEl={anchorRef.current}
        sx={{ width: 220 }}
      >
        <Box sx={{ my: 1.5, px: 2.5 }}>
          <Typography
            variant='subtitle1'
            noWrap
          >
            {`${loggedInUserInfo.name}`}
          </Typography>
          <Typography
            variant='body2'
            sx={{ color: 'text.secondary' }}
            noWrap
          >
            {loggedInUserInfo.email}
          </Typography>
        </Box>

        <Divider sx={{ my: 1 }} />

        {menuOptions.map((option) => (
          <MenuItem
            key={option.label}
            to={
              location?.pathname === '/organizations'
                ? '/profile-view'
                : option.linkTo
            }
            component={RouterLink}
            onClick={handleClose}
            sx={{ typography: 'body2', py: 1, px: 2.5 }}
          >
            <Iconify
              icon={option.icon}
              sx={{
                mr: 2,
                width: 24,
                height: 24,
              }}
            />
            {option.label}
          </MenuItem>
        ))}

        {/* {userRole === 'superAdmin' && (
          <MenuItem
            key={'label.profile'}
            to={
              location?.pathname === '/organizations'
                ? '/profile-view'
                : '/user/profile'
            }
            component={RouterLink}
            onClick={handleClose}
            sx={{ typography: 'body2', py: 1, px: 2.5 }}
          >
            <Iconify
              icon={'eva:person-fill'}
              sx={{
                mr: 2,
                width: 24,
                height: 24,
              }}
            />
            Profile
          </MenuItem>
        )} */}

        <Box sx={{ p: 2, pt: 1.5 }}>
          <Button
            fullWidth
            color='inherit'
            variant='outlined'
            onClick={handleLogout}
          >
            Logout
          </Button>
        </Box>
      </MenuPopover>
    </>
  );
};
export default AccountPopover;
